import React, { useState } from 'react';
import classnames from 'classnames';
import { useForm, useField } from 'react-final-form-hooks';
import { Button, Form, FormProps, Input, Modal, ModalProps } from 'tea-component';
import IconButton from '@gp/components/IconButton';
import TagInput from '@gp/components/TagInput';
import TinyEditor from '@gp/components/TinyEditor';
import EmailSenderTest from './components/EmailSenderTest';
import { isEmptyValue } from '@home/utils/common';
import {
  isEmptyList,
  reportBtnOperationLog,
  formatTeaFormItemStatus,
  formatTeaFormItemMessage,
} from '@gp/utils/common';
import { validateEmail } from '@gp/utils/validators';
import { validateRecipients, notificationSendEmailFeedback } from './utils';
import { EmailSendType, EmailSendResultStatus } from './enums';
import { FIELD_COMMON_ERR_MSG } from '@gp/constants/const';
import { TAG_INPUT_PLACEHOLDER } from './constants';
import { useAutoSaveParams, EDIT_PAGE_KEY_MAP } from '@gp/hooks/useAutoSaveParams';
import type { IBaseProps } from '@home/types/common';
import type { IRecord } from '@gp/types/global';
import type { IEmailVO, IEmailSendResult } from './types';
import AutoSaveModal from '@gp/components/AutoSaveModal';
import './index.scss';

export interface IEmailSenderProps extends IBaseProps {
  disabled?: boolean;
  isSending?: boolean;
  isTestSending?: boolean;
  sendLogKey?: string;
  header?: React.ReactElement | null;
  modalProps?: ModalProps;
  formProps?: FormProps;
  onSend?: (email: IEmailVO) => Promise<IEmailSendResult>;
  onTest?: (email: IEmailVO) => Promise<IEmailSendResult>;
  onCancel?: () => void;
  onOpen?: () => void;
}

const DEFAULT_FORM_MODEL: IEmailVO = {
  recipients: {
    tags: [],
    input: '',
  },
  title: '',
  content: '',
  send_type: '',
};

const IGNORE_VALIDATE_KEYS = ['content', 'send_type'];
const handleValidate = (values?: IEmailVO): IRecord<string> => {
  if (!values) return { recipients: FIELD_COMMON_ERR_MSG, title: FIELD_COMMON_ERR_MSG };
  const errors: IRecord<string> = {};
  const entries = Object.entries(values);
  for (const entry of entries) {
    const [key, value] = entry;
    if (IGNORE_VALIDATE_KEYS.includes(key)) continue;
    if (key === 'recipients') {
      validateRecipients(errors, value);
    }
    if (isEmptyValue(value)) {
      errors[key] = FIELD_COMMON_ERR_MSG;
    }
  }
  return errors;
};

const getIsFormAvailable = (values?: IEmailVO): boolean => {
  const { recipients, title, content } = values || {};
  const { tags, input } = recipients || {};
  return !isEmptyList(tags) || !!input?.trim() || !!title?.trim() || !!content?.trim();
};

const EmailSender: React.FC<IEmailSenderProps> = (props) => {
  const {
    style,
    className,
    disabled = false,
    isSending = false,
    isTestSending = false,
    sendLogKey,
    modalProps,
    formProps,
    header,
    onSend,
    onTest,
    onCancel,
    onOpen,
  } = props;
  const { style: modalStyle, className: modalClassName, ...restModalProps } = modalProps || {};
  const { style: formStyle, className: formClassName, ...restFormProps } = formProps || {};

  const [visible, setVisible] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [editAutoSaveData, setEditAutoSaveData] = useState(DEFAULT_FORM_MODEL);

  const handleConfirm = async (values: IEmailVO) => {
    try {
      const res = await onSend?.({ ...values, send_type: EmailSendType.Official });
      const { status, title, emails } = res || {};
      notificationSendEmailFeedback(res, title, emails);
      if (status === EmailSendResultStatus.Success) {
        deleteSaveParamsByPageKey(pageKeyCur);
        handleClose();
      }
    } catch (exception) {}
  };

  const { form, values, handleSubmit } = useForm({
    initialValues: editAutoSaveData,
    onSubmit: handleConfirm,
    validate: handleValidate,
  });

  const formAvailable = getIsFormAvailable(values);

  const pageKeyCur = `${EDIT_PAGE_KEY_MAP.pipeline_DPSNewsletter_send_email}`;

  // 得像添加项目一样，监控是否有手动改动
  // useEffect(() => {
  //   if (JSON.stringify(DEFAULT_FORM_MODEL) === JSON.stringify(values) && formAvailable) {
  //     deleteSaveParamsByPageKey(pageKeyCur);
  //   }
  // }, [values, formAvailable]);

  // 自动保存hooks
  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } = useAutoSaveParams({
    pageKey: pageKeyCur,
    initialParams: DEFAULT_FORM_MODEL,
    saveParamsCur: values,
    isBegin: !isSending && visible,
    isChangeValue: true,
  });

  const handleAutoSaveConfirm = () => {
    const saveObj = getSaveParamsValue(pageKeyCur) || {};
    setEditAutoSaveData(saveObj);
    setAutoSaveVisible(false);
    setVisible(true);
    onOpen?.();
  };
  const handleAutoSaveCancel = () => {
    deleteSaveParamsByPageKey(pageKeyCur);
    // form?.restart();
    setEditAutoSaveData(DEFAULT_FORM_MODEL);
    setAutoSaveVisible(false);
    setVisible(true);
    onOpen?.();
  };

  const recipientsField = useField('recipients', form);
  const titleField = useField('title', form);
  const contentField = useField('content', form);

  const handleSubmitInPage = () => {
    // deleteSaveParamsByPageKey(pageKeyCur);
    setSubmitted(true);
    handleSubmit?.();
    reportBtnOperationLog(sendLogKey);
  };

  const handleClose = () => {
    deleteSaveParamsByPageKey(pageKeyCur);
    setSubmitted(false);
    setVisible(false);
    form?.restart();
  };

  const handleSendEmail = () => {
    if (showRestorePrompt(pageKeyCur)) {
      setAutoSaveVisible(true);
    } else {
      setEditAutoSaveData(DEFAULT_FORM_MODEL);
      setVisible(true);
      onOpen?.();
    }
  };

  return (
    <>
      <IconButton
        style={style}
        className={classnames('dbg-common-white-btn', className, { 'is-disabled': disabled })}
        type="text"
        disabled={disabled}
        onClick={() => {
          handleSendEmail();
        }}
      >
        Send Email
      </IconButton>
      <Modal
        {...restModalProps}
        style={modalStyle}
        className={classnames('dbg-common-email-sender__modal', modalClassName)}
        caption="Email Setting"
        size="l"
        visible={visible}
        disableCloseIcon={isSending}
        onClose={() => {
          handleClose();
        }}
      >
        <Modal.Body>
          <Form
            {...restFormProps}
            style={formStyle}
            className={classnames('dbg-common-email-sender__form', formClassName)}
            layout="fixed"
            fixedLabelWidth={106}
          >
            {header}
            <Form.Item
              label="Recipients"
              required
              showStatusIcon={false}
              status={formatTeaFormItemStatus(submitted, recipientsField?.meta)}
              message={formatTeaFormItemMessage(submitted, recipientsField?.meta)}
            >
              <TagInput {...recipientsField?.input} placeholder={TAG_INPUT_PLACEHOLDER} validateTag={validateEmail} />
            </Form.Item>
            <Form.Item
              label="Email Title"
              required
              showStatusIcon={false}
              status={formatTeaFormItemStatus(submitted, titleField?.meta)}
              message={formatTeaFormItemMessage(submitted, titleField?.meta)}
            >
              <Input {...titleField?.input} placeholder="Please input Email Title" />
            </Form.Item>
            <Form.Item label="Email Body" showStatusIcon={false}>
              <TinyEditor
                synchronize
                className={classnames('dbg-common-editor gp-word-break-word dbg-common-email-sender__editor', {
                  'dbg-common-editor--disabled': false,
                })}
                placeholder="Please fill in email body to introduce the publishing DPS Newsletter"
                value={contentField?.input?.value}
                getEditorContent={(payload) => {
                  contentField?.input?.onChange(payload || '');
                }}
                getEditorText={() => {}}
                getIsFocused={() => {}}
              />
            </Form.Item>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            disabled={!formAvailable}
            loading={isSending}
            onClick={(e) => {
              e?.stopPropagation();
              handleSubmitInPage();
            }}
          >
            Send
          </Button>
          <EmailSenderTest
            disabled={isSending || !values?.title}
            isTestSending={isTestSending}
            modalProps={modalProps}
            formProps={formProps}
            onTest={async (payload) => {
              const emailTmp = { ...values, ...payload, send_type: EmailSendType.Test };
              const res = await onTest?.(emailTmp);
              const { title, emails } = res || {};
              notificationSendEmailFeedback(res, title, emails);
              return res;
            }}
          />
          <Button
            disabled={isSending}
            onClick={(e) => {
              e?.stopPropagation();
              deleteSaveParamsByPageKey(pageKeyCur);
              handleClose();
              onCancel?.();
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 咨询自动保存弹窗 */}
      <AutoSaveModal visible={autoSaveVisible} onConfirm={handleAutoSaveConfirm} onClose={handleAutoSaveCancel} />
    </>
  );
};

export default EmailSender;
